import React from 'react'
import Markdown from 'react-markdown'
import { each, map, toPairs } from 'lodash/fp'

import { Caption, Container, FormattedMessage, Page } from '@/components'
import { interpolateUnderline } from '@/utils'
import { JobOfferDescription, PageContext } from '@/interfaces'

import JobsDescriptionBanner from '../JobsDescriptionBanner'
import JobsDescriptionIntroduction from '../JobsDescriptionIntroduction'
import BlackCircle from './images/black-circle.svg'

import qlData from './JobsDescription.data'
import styles from './JobsDescription.module.css'

const JobsDescription: React.FC<PageContext> = ({ data, pageContext }) => {
  const { jobOffer } = qlData(data)

  const getDynamicId = (index: string, name: string) =>
    `${pageContext.slug}.description.${index}.${name}`

  // For now it's a reliable way to add dynamic translations without
  // polluting console with undefined translation errors.
  each(([index, { title }]) => {
    // eslint-disable-next-line no-param-reassign
    pageContext.intl.messages[getDynamicId(index, 'title')] = title
  }, toPairs<JobOfferDescription>(jobOffer.descriptions))

  return (
    <Page>
      <Container narrow as="section" className={styles.container}>
        <JobsDescriptionIntroduction jobOffer={jobOffer} />
        <div className={styles.descriptions}>
          {map(
            ([index, { description, bullets }]) => (
              <div key={index} className={styles.description}>
                <Caption
                  variant="h4"
                  tag="h2"
                  className={styles.descriptionTitle}>
                  <FormattedMessage
                    id={getDynamicId(index, 'title')}
                    values={{
                      ...interpolateUnderline(),
                    }}
                  />
                </Caption>
                <Caption variant="p2" className={styles.descriptionText}>
                  <Markdown
                    source={description}
                    renderers={{ paragraph: ({ children }) => children }}
                  />
                </Caption>
                <ul className={styles.bullets}>
                  {map(
                    (bullet) => (
                      <li key={bullet} className={styles.bulletLine}>
                        <div className={styles.bulletWrapper}>
                          <Caption variant="p2" className={styles.bulletGhost}>
                            *
                          </Caption>
                          <BlackCircle className={styles.blackCircle} />
                        </div>
                        <Caption variant="p2" className={styles.bullet}>
                          {bullet}
                        </Caption>
                      </li>
                    ),
                    bullets,
                  )}
                </ul>
              </div>
            ),
            toPairs<JobOfferDescription>(jobOffer.descriptions),
          )}
        </div>
      </Container>
      <JobsDescriptionBanner offerSlug={jobOffer.slug} />
    </Page>
  )
}

export default JobsDescription
